import { Directive, EventEmitter, Injector, OnInit, Output } from '@angular/core';
import { Observable, Subscription, catchError, forkJoin, map, of, startWith, tap } from 'rxjs';
import { ServiceBase, SnackbarFactory } from '@shared/services';
import { ActivatedRoute, Router } from '@angular/router';
import { SelectListItem } from '@shared/models';
import { formatDate } from '@angular/common';
import { isArray, toBoolean } from '@datorama/akita';
import { HttpErrorResponse } from '@angular/common/http';
import { FormControl } from '@angular/forms';

@Directive()
export abstract class FilterToolbarBaseDirective<T> implements OnInit {

  @Output() refreshButtonPressed = new EventEmitter<any>();
  @Output() queryParams = new EventEmitter<any>();

  protected snackBar: SnackbarFactory;
  protected router: Router;
  protected activatedRoute: ActivatedRoute;
  protected $loadDataSubscription: Subscription;
  protected filterModels: Array<FilterModel>;
  public htmlRows: Array<number>;

  private lastQueryParams: any = null;

  constructor(
    injector: Injector,
    protected service: ServiceBase<T>,
  ) {
    this.snackBar = injector.get(SnackbarFactory);
    this.router = injector.get(Router);
    this.activatedRoute = injector.get(ActivatedRoute);
  }

  protected get selectListModels(): FilterModel[] {
    return this.filterModels?.filter(fm => (["ddl", "ddlMultiple", "searchDdl", "searchMultiDdl"] as FilterType[]).includes(fm.type)).filter(fm => !fm.selectList || fm.selectList?.length == 0);
  }

  ngOnInit(): void {
    //route-based setting of filters
    this.activatedRoute.queryParams.subscribe({
      next: (params) => {
        let keys = Object.keys(params).map(k => k.toLowerCase());

        this.filterModels?.forEach(fm => {
          if (fm.type == "dateRange") {
            let start = fm.value?.start;
            let end = fm.value?.end;
            let startKey = `${fm.name}Start`.toLowerCase();
            let endKey = `${fm.name}End`.toLowerCase();
            if (keys.includes(startKey)) {
              start = new Date(params[startKey]);
            }
            if (keys.includes(endKey)) {
              end = new Date(params[endKey]);
            }
            fm.value = new DateRangeModel(start, end);
          }
          else if (keys.includes(fm.name.toLowerCase())) {
            let fmName = fm.name.toLowerCase();
            switch (fm.type) {
              case "date":
              case "datetime":
                fm.value = new Date(params[fmName]);
                break;
              case "ddlMultiple":
                let array = isArray(params[fmName]) ? params[fmName] : [params[fmName]];
                fm.value = array.map(val => fm.numericId ? parseInt(val) : val.toString());
                break;
              case "ddl":
                let val = params[fmName];
                fm.value = fm.numericId ? parseInt(val) : val.toString();
                break;
              case "checkbox":
                fm.value = toBoolean(params[fmName]);
                break;
              case "numeric":
                fm.value = parseInt(params[fmName]);
                break;
              default:
                fm.value = params[fmName];
                break;
            }
          }
        })

        if (this.filterModels.some(fm => fm.value != null)) {
          this.queryParamsChanged();
        }
      }
    });

    if (!this.htmlRows) {
      this.htmlRows = [this.filterModels.length];
    }
    else {
      let count = 0;
      this.htmlRows.map(r => count += r);
      if (count != this.filterModels.length) {
        this.htmlRows[this.htmlRows.length - 1] += (this.filterModels.length - count)
      }
    }

    let subscription = this.getSelectlists().subscribe({
      complete: () => {
        this.queryParamsChanged();
        this.refresh();
      }
    });

    this.setObservableSubscriptions(subscription);
  }

  public onChangeEvent($event, inputName: string) {
    this.queryParamsChanged();
  }

  public clearInput(inputName: string) {
    let model = this.filterModels.find(fm => fm.name == inputName);
    switch (model.type) {
      case "ddlMultiple":
        model.value = [];
        break;
      case "dateRange":
        model.value = new DateRangeModel();
        break;
      case "checkbox":
        model.value = false;
        break;
      default:
        model.value = null;
        break;
    }
    this.queryParamsChanged();
  }

  public clearAllFilters() {
    this.filterModels.forEach(fm => this.clearInput(fm.name));
  }

  protected queryParamsChanged(forceReload: boolean = false) {
    let deepEqual = (x, y) => {
      return (x && y && typeof x === 'object' && typeof y === 'object') ?
        (Object.keys(x).length === Object.keys(y).length) &&
        Object.keys(x).reduce(function (isEqual, key) {
          return isEqual && deepEqual(x[key], y[key]);
        }, true) : (x === y);
    }

    let currentQueryParams = { ...this.getQueryParams };

    if (!deepEqual(this.lastQueryParams, currentQueryParams) || forceReload) {
      this.queryParams.emit(currentQueryParams);
      this.setRouteQueryParams();
      this.lastQueryParams = { ...currentQueryParams };
    }
  }

  protected setRouteQueryParams() {
    let url = this.router.createUrlTree([''], {
      queryParams: this.getQueryParams
    })
    let searchString = url.toString().split('?')[1] ?? null;
    window.history.replaceState({}, '', `${location.origin}${location.pathname}${searchString ? '?' + searchString : ''}`)
  }

  protected get getQueryParams(): object {
    let params = {};

    this.filterModels?.forEach(filter => {
      if (filter.value != null) {
        let transformedValue;

        switch (filter.type) {
          case "date":
            transformedValue = formatDate(filter.value, 'yyyy-MM-dd', 'en');
            break;
          case "datetime":
            transformedValue = formatDate(filter.value, 'yyyy-MM-dd HH:mm:ss', 'en');
            break;
          case "dateRange":
            transformedValue = {};
            if (filter.value?.start) {
              transformedValue[`${filter.name}Start`] = formatDate(filter.value.start, 'yyyy-MM-dd', 'en');
            }
            if (filter.value?.end) {
              transformedValue[`${filter.name}End`] = formatDate(filter.value.end, 'yyyy-MM-dd', 'en');
            }
            break;
          default:
            transformedValue = filter.value
            break;
        }
        if (typeof (transformedValue) == "object" && !isArray(transformedValue)) {
          params = { ...params, ...transformedValue };
        }
        else {
          params[filter.name] = transformedValue;
        }
      }
    });
    return params;
  }

  protected getSelectlists(): Observable<any> {
    let observableList: Observable<any>[] = [];

    this.selectListModels.forEach(model => {
      let func = this.getSelectList(model);
      if (model.observable) {
        observableList.push(func);
      } else {
        model.subscription = func.subscribe();
      }
    });

    return observableList.length > 0 ? forkJoin(observableList) : of(1);
  }

  protected setObservableSubscriptions(subsciption: Subscription) {
    this.selectListModels.filter(fm => fm.observable).forEach(fm => {
      fm.subscription = subsciption;
    })
  }

  protected refresh() {
    this.refreshButtonPressed.emit();
  }

  protected loadData(functionToDo: (res: T[]) => any) {
    this.$loadDataSubscription?.unsubscribe();

    this.$loadDataSubscription = this.service.get({ ...this.getQueryParams }).subscribe({
      next: (res) => {
        functionToDo(res);
      },
      error: (err) => {
        this.snackBar.showButtonSnackbar(err);
      }
    });
  }

  protected getSelectList(filterModel: FilterModel): Observable<any> {
    let notifyParamsChange = (forceReload = false) => {
      if (!filterModel.observable || forceReload) {
        this.queryParamsChanged(forceReload);
      }
    };

    return this.getRefSelectlist(filterModel).pipe(
      tap(({ selectItems, defaultItem }) => {
        filterModel.selectList = selectItems;
        switch (filterModel.type) {
          case "ddl":
            if (!filterModel.value && defaultItem) {
              filterModel.value = defaultItem.id;
              notifyParamsChange();
            }
            else {
              if (filterModel.numericId && !filterModel.selectList.find(item => item.id == filterModel.value)) {
                filterModel.value = null;
                notifyParamsChange();
              }
              if (!filterModel.numericId) {
                let matchItem = filterModel.selectList.find(item => item.id.toString().toLowerCase() == filterModel.value?.toString().toLowerCase());
                if (matchItem && matchItem !== filterModel.value) {
                  filterModel.value = matchItem.id;
                  notifyParamsChange();
                }
                else {
                  filterModel.value = null;
                  notifyParamsChange();
                }
              }
            }
            break;
          case "ddlMultiple":
            if (filterModel.value?.length == 0 && defaultItem) {
              filterModel.value = [defaultItem.id];
              notifyParamsChange();
            }
            else {
              let notAvailableValues = [];
              (filterModel.value as any[]).forEach(val => {
                if (!filterModel.selectList.find(item => item.id == val)) {
                  notAvailableValues.push(val);
                }
              });
              if (notAvailableValues.length > 0) {
                notAvailableValues?.forEach(val => {
                  let idx = (filterModel.value as any[]).findIndex(value => value == val);
                  (filterModel.value as any[]).splice(idx, 1);
                })
                notifyParamsChange(true);
              }
            }
            break;
          case "searchDdl":
          case "searchMultiDdl":
            this.setFilteredSelectList(filterModel);
            filterModel.formControl.enable();
            break;
          default:
            if (!filterModel.selectList.find(item => item.id == filterModel.value)) {
              filterModel.value = null;
            }
            break;
        }
      }),
      catchError((err, caught) => {
        if (err instanceof HttpErrorResponse) {
          this.snackBar.showButtonSnackbar(err);
        }
        else {
          this.snackBar.showButtonSnackbar(new HttpErrorResponse({ statusText: err, status: 400 }));
        }
        return of(1);
      })
    )
  }

  protected getRefSelectlist(filterModel: FilterModel) {
    return this.service.getRefSelectList(`selectlist/${filterModel.endpoint ?? filterModel.name}`, { listName: filterModel.listName, ...filterModel.params });
  }

  public filterSearchDdl(filterModel: FilterModel) {
    if (filterModel.formControl?.value !== null && filterModel.formControl?.value !== '') {
      filterModel.formControl?.setValue(null);
      this.setFilteredSelectList(filterModel);
    }
  }

  protected setFilteredSelectList(filterModel: FilterModel) {
    filterModel.filteredSelectList = filterModel.formControl?.valueChanges.pipe(
      startWith((filterModel.formControl?.value ?? filterModel.formControl?.value?.text) ?? ''),
      map((value) => this._filter(value || '', [...filterModel.selectList]))
    );
  }

  private _filter(value: string, array: SelectListItem[]): SelectListItem[] {
    let filteredValues = [...array].filter((option) => option.text.toLowerCase().includes(value.toLowerCase())).sort();
    return filteredValues;
  }

  ngOnDestroy() {
    if (this.$loadDataSubscription) {
      this.$loadDataSubscription.unsubscribe();
    }
    this.filterModels?.forEach(model => {
      if (model?.subscription) {
        model.subscription.unsubscribe();
      }
    })
  }
}

export type FilterType = "ddl" | "ddlMultiple" | "date" | "datetime" | "dateRange" | "search" | "checkbox" | "numeric" | "searchDdl" | "searchMultiDdl";

export class FilterModel {
  constructor(init: {
    name: string,
    type: FilterType,
    value?: any,
    selectList?: SelectListItem[],
    filteredSelectList?: Observable<SelectListItem[]>,
    observable?: boolean,
    endpoint?: string,
    listName?: string,
    numericId?: boolean,
    params?: object,
    subscription?: Subscription,
    formControl?: FormControl,
  }) {
    this.name = init.name;
    this.type = init.type;

    let setValue: any;
    if (init.value) {
      setValue = init.value;
    }
    else {
      switch (init.type) {
        case "dateRange":
          setValue = new DateRangeModel();
          break;
        case "ddlMultiple":
          setValue = [];
          break;
        case "checkbox":
          setValue = false;
          break;
        default:
          setValue = null;
      }
    }
    this.value = setValue;

    this.selectList = init.selectList ?? (init.type.startsWith('ddl') ? [] : null);
    this.filteredSelectList = init.filteredSelectList ?? null;
    this.observable = init.observable ?? (init.type.startsWith('ddl') ? false : null);
    this.endpoint = init.endpoint ?? null;
    this.listName = init.listName ?? null;
    this.params = init.params ?? {};
    this.numericId = init.numericId ?? true;
    this.subscription = init.subscription ?? null;
    this.formControl = init.formControl ?? new FormControl({ value: null, disabled: true });
  }

  name: string;
  type: FilterType;
  value?: any | DateRangeModel;
  selectList?: SelectListItem[];
  filteredSelectList?: Observable<SelectListItem[]>;
  observable?: boolean;
  endpoint?: string;
  listName?: string;
  numericId?: boolean;
  params?: object;
  subscription?: Subscription;
  formControl: FormControl;
}

export class DateRangeModel {
  constructor(start?: Date, end?: Date) {
    this.start = start ?? null;
    this.end = end ?? null;
  }

  start?: Date;
  end?: Date;
}
